/**
 * SearchInput Component
 *
 * A debounced search input with clear button and loading state.
 * Uses internal state for immediate display while providing debounced
 * onChange callback for efficient API calls or filtering operations.
 *
 * @example
 * ```tsx
 * // Basic usage
 * const [searchTerm, setSearchTerm] = useState('');
 * <SearchInput
 *   value={searchTerm}
 *   onChange={setSearchTerm}
 *   placeholder="Search tasks..."
 * />
 *
 * // With loading state and custom debounce
 * <SearchInput
 *   value={searchTerm}
 *   onChange={setSearchTerm}
 *   placeholder="Search..."
 *   debounceMs={500}
 *   isLoading={isSearching}
 * />
 *
 * // With custom left section
 * <SearchInput
 *   value={searchTerm}
 *   onChange={setSearchTerm}
 *   leftSection={<IconFilter size={16} />}
 * />
 * ```
 */

import type { ChangeEvent, KeyboardEvent } from 'react';
import { memo, useState, useEffect, useCallback, useRef } from 'react';
import { TextInput, ActionIcon, Loader } from '@mantine/core';
import { IconSearch, IconX } from '@tabler/icons-react';
import { useDebounce } from '@/hooks';
import type { SearchInputProps } from './SearchInput.types';
import styles from './SearchInput.module.css';

/** Default debounce delay in milliseconds */
const DEFAULT_DEBOUNCE_MS = 300;

/** Maximum wait time before forcing debounce execution */
const MAX_WAIT_MS = 1000;

/** Icon size for search and clear icons */
const ICON_SIZE = 16;

/** Loader size for loading spinner */
const LOADER_SIZE = 'xs';

export const SearchInput = memo(function SearchInput({
  value,
  onChange,
  placeholder,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  leftSection,
  isLoading = false,
  autoFocus = false,
  className,
}: SearchInputProps) {
  // Internal state for immediate display
  const [localValue, setLocalValue] = useState(value);
  const previousDebouncedValueRef = useRef<string>(value);

  // Debounce the local value
  const { debouncedValue, cancel } = useDebounce(localValue, debounceMs, {
    trailing: true,
    maxWait: MAX_WAIT_MS,
  });

  // Sync local value when external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Call onChange when debounced value changes
  useEffect(() => {
    if (debouncedValue !== previousDebouncedValueRef.current) {
      previousDebouncedValueRef.current = debouncedValue;
      onChange(debouncedValue);
    }
  }, [debouncedValue, onChange]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      cancel();
    };
  }, [cancel]);

  // Handle input change
  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setLocalValue(event.target.value);
  }, []);

  // Handle clear button click
  const handleClear = useCallback(() => {
    setLocalValue('');
    onChange('');
  }, [onChange]);

  // Handle keyboard events (Escape to clear)
  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleClear();
    }
  }, [handleClear]);

  // Render right section (clear button or loader)
  const renderRightSection = () => {
    if (isLoading) {
      return (
        <Loader size={LOADER_SIZE} className={styles.loader} />
      );
    }

    if (localValue) {
      return (
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          onClick={handleClear}
          aria-label="Clear search"
          className={styles.clearButton}
        >
          <IconX size={ICON_SIZE} />
        </ActionIcon>
      );
    }

    return null;
  };

  return (
    <div className={styles.wrapper}>
      <TextInput
        className={`${styles.input} ${className ?? ''}`}
        value={localValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        leftSection={leftSection ?? <IconSearch size={ICON_SIZE} />}
        rightSection={renderRightSection()}
        autoComplete="off"
        autoFocus={autoFocus}
      />
    </div>
  );
});

SearchInput.displayName = 'SearchInput';
